/**
 *
 * MarketPage
 *
 */

import { Button, Col, Form, Pagination, Row, Skeleton } from 'antd';
import {
  selectAddedProducts,
  selectAddingProducts,
  selectBreadCrumbDataMarkup,
  selectProductTags,
  selectSelectedCategory,
  selectStore,
  selectUser,
  selectUserPermissions,
} from 'containers/MainLayout/selectors';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { compose, Dispatch } from 'redux';

import ModalNote from 'components/Checkout/ProductNoteModal';
import NewMarketItem from 'components/MarketItem/NewMarketItem';

import PageHeader from 'components/PageHeader/PageHeader';
import { setFavorite, setOpenSigninPopup, setProductNote } from 'containers/MainLayout/actions';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import {
  Category,
  Product,
  ProductSortField,
  QueryProductSearchArgs,
  SortDirection,
  Store,
  Tag,
  TrackingAddToCartAddFromType,
  TrackingProductImpressionType as TrackingType,
  User,
} from 'types/schema';
import injectReducer from 'utils/injectReducer';
import injectSaga from 'utils/injectSaga';
import media from 'utils/mediaStyle';
import * as qs from 'utils/queryString';
import renderCategoryName from 'utils/renderCategoryName';
import { applySearch, sendRequestNewProductAction } from './actions';
import messages from './messages';
import reducer from './reducer';
import saga from './saga';
import selectMarketPage, { selectAggregationData } from './selectors';
import { ContainerState, RootState } from './types';

import { DownOutlined } from '@ant-design/icons';
import PaginationLink from 'components/PaginationLink';
import ReadMoreButton from 'components/ReadMore';
import RenderMetadataMarket, { IMetadataKeyData } from 'components/RenderMetadataMarket';
import RequestNewProductPopup from 'components/RequestNewProductPopup';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import { Viewport } from 'components/ViewportProvider';
import { SkeletonWrapper } from 'containers/ProductDetailsPage';
import useReadMore from 'hooks/useReadMore';
import { isEmpty } from 'lodash';
import translations from 'translations';
import { commonDimensions, KAMEREO_SUPPLIER_ID } from 'utils/constants';
import generateProductSlug, { convertStringToSlug } from 'utils/generateProductSlug';
import { createSearchQueryfromSlug, getPathSegmentFromSlug } from 'utils/getPathSegmentFromSlug';
import { EventName, sendGA4Event } from 'utils/triggerGA4Event';
import { pathnameChecker } from 'utils/url';
import { isRecommendationTag } from 'utils/utilities';
import keywordData from '../../../sitemap/keywordData.js';
import useProductDetailContent from '../../components/Image/useProductDetailContent';
import SubCategoryNavBarDesktopComponent from '../SubCategoryNavBarDesktop';
import KamereoPromotionProducts from './KamereoPromotionProducts';
import { recommendedForYouKey } from './metadata/recommended';
import NotFoundProductImage from './noSearchResult.svg';
import { SearchFilter, SearchFilterModal } from './SearchFilter';
import takeOutTheMetaFromDescription from './TakeOutTheMeta';
import { handleParseQueryParams } from './utils';

interface ITrackingFrom {
  productImpression: TrackingType;
  addToCart: TrackingAddToCartAddFromType;
}

const FlexContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.div`
  color: #475a5f;
  font-size: 16px;
  margin: 15px 0 6px 0;
`;

const SuggestedListTitle = styled.div`
  color: #424242;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 28px;
  margin: 40px 0px 24px;
`;

const ProductListRow = styled(Row)`
  &.ant-row {
    display: flex;
    flex-wrap: wrap;
  }

  > .ant-col {
    &:not(:last-child) {
      // border-bottom: 1px solid #dce0e9;
      ${media.md`
        border-bottom: none;
      `};
    }
    @media (max-width: 768px) {
      width: 100%;
    }
  }

  @media (min-width: ${commonDimensions.MAX_WIDTH_CONTAINER}px) {
    .ant-col-xl-5 {
      width: 20%;
    }
  }
`;

const ProductListContainer = styled.div`
  min-height: calc(100vh - 246px);
  display: flex;
  flex-direction: column;
  margin: 16px 0;
  background: #ffffff;
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.25);
  border-radius: 4px;
  padding: 12px;
  ${media.md`
    padding: 24px;
    min-height: calc(100vh - 155px);
  `};

  ${media.lg`
    min-height: calc(100vh - 53px);
  `};
  @media (max-width: 992px) {
    margin-top: 8px;
  }
`;

const ProductDescriptionWrapper = styled.div`
  display: grid;
  grid-template-rows: min-content 1fr;
  gap: 24px;
  width: 100%;
  min-height: 384px;
  padding: 24px;
  border-radius: 8px;
  background-color: hsla(0, 0%, 100%, 1);
  box-shadow: 0px 0px 2px 0px hsla(0, 0%, 0%, 0.2);
  border: none;
`;

const PageDescription = styled.div`
  max-height: 240px;
  overflow: hidden;

  * {
    color: #424242;
    font-family: 'SF Pro Text', 'Arial', sans-serif !important;
    font-weight: 400;
    line-height: 16px;
  }
  *:not(h1, h2, h3, h4, h5, h6) {
    font-size: 14px !important;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    padding-top: 10px !important;
    font-weight: 500;
  }
  & > * + *:not(:last-child) {
    margin-top: 8px !important;
  }
  p:has(> span img) {
    max-width: 100%;
    text-align: center !important;
  }
  ul {
    li {
      padding-top: 0 !important;
      padding-bottom: 0 !important;
    }
    li:not(:last-child) {
      margin-bottom: 8px !important;
    }
  }
  table {
    margin-inline: auto !important;
  }

  ${({ isShowFullDescription }) =>
    isShowFullDescription &&
    `
    max-height: none;
    overflow: hidden;
    `}
`;

const PaginationWrapper = styled(Row)`
  margin-top: auto;
  .ant-pagination {
    margin-top: 24px;
  }
`;

const DownArrow = styled(DownOutlined)`
  width: 14px;
  height: 14px;
  aspect-ratio: 1;
`;

const NotFoundImage = styled(SVGIcon)`
  width: 108px;
  height: 108px;
`;

const ButtonWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 22px;
  width: 100%;
  max-width: 422px;
  margin-top: 16px;
`;

const ButtonRequestNewProduct = styled(Button)`
  width: fit-content;
  margin: 0 auto 16px;
  color: #4b9c63 !important;
  border-color: #4b9c63 !important;
  font-size: 16px;
  font-weight: 500;
  height: 40px;
  &.ant-btn-default:hover {
    color: #ffffff !important;
    background: #4b9c63 !important;
  }
`;

interface IStateProps {
  marketpage: ContainerState;
  category: Category | null | undefined;
  addedProducts: any;
  addingProducts: any;
  permissions: string[];
  breadCrumbDataMarkup: string;
  store: Store;
  user: User;
  productTags: Tag[];
  aggregationData: any;
  openSearchFilter?: boolean;
  setOpenSearchFilter?: (state: boolean) => void;
  canClearFilter?: boolean;
  setCanClearFilter?: (state: boolean) => void;
}

interface IDispatchProps {
  onSearch: (data: any) => void;
  onToggleFavorite: (data: any) => void;
  setProductNote: (data: any) => void;
  gotoSignIn: () => void;
  sendRequestNewProduct: (data: any) => void;
}

type Props = IStateProps & IDispatchProps & RouteComponentProps;

const MarketPage: FC<Props> = (props) => {
  const {
    marketpage: { result, description, search, noteItemLoading, kamereoProducts, suggestedProducts },
    permissions,
    category,
    addedProducts,
    addingProducts,
    onSearch,
    onToggleFavorite,
    setProductNote,
    gotoSignIn,
    sendRequestNewProduct,
    breadCrumbDataMarkup,
    location,
    history,
    store,
    user,
    productTags,
    aggregationData,
    openSearchFilter,
    setOpenSearchFilter,
    canClearFilter,
    setCanClearFilter,
  } = props;
  const [form] = Form.useForm();
  const lang = localStorage.getItem('lang') || 'vi';
  const loggedIn = !!localStorage.getItem('token');
  const { localLang, alternateLang } = result;
  const [previewProduct, setPreviewProduct] = useState<any>();
  const [isOpenNoteModal, setIsOpenNoteModal] = useState<boolean>(false);
  const [isOpenRequestProductModal, setIsOpenRequestProductModal] = useState<boolean>(false);
  const [persistFilter, setPersistFilter] = useState<any>({});
  const [isInitPage, setIsInitPage] = useState<any>(false);

  const contentRef = useRef<HTMLDivElement>(null);
  const scrollPosition = (contentRef?.current?.scrollHeight ?? 0) + 100;
  const descriptionByLang = (localLang === 'vi' ? description?.descriptionVn : description?.description) || '';
  const [isShowFullDescription, handleReadMore] = useReadMore(description.nameLocal, scrollPosition);
  const refinedDescription = useProductDetailContent(
    descriptionByLang,
    description.nameLocal,
    takeOutTheMetaFromDescription,
  );

  const onChangePage = useCallback(
    (page: number) => {
      if (!location.pathname.startsWith('/market')) {
        onSearch({
          search: {
            ...search,
            pagination: {
              page: page !== 1 ? page : undefined,
            },
          },
          isFromCategory: true,
        });
      } else {
        const formValue = form.getFieldsValue(['origins', 'brands', 'subCategory', 'sort']);
        handleAllChangeSearch(formValue, { page: page !== 1 ? page : undefined });
      }
      window.scrollTo(0, 0);
      sendGA4Event(EventName.SEARCH_PAGE_PAGINATION_CLICK, {
        page_number: page,
        keyword: search?.filter?.query || '',
        total_pages: result?.totalPage,
      });
    },
    [search, onSearch, location.pathname, result?.totalPage],
  );

  const onClickTogglingFavorite = useCallback(
    (product) => {
      onToggleFavorite({ product: product });
    },
    [onToggleFavorite],
  );

  const onClickCloseNoteModal = useCallback(() => {
    setPreviewProduct(undefined);
    setIsOpenNoteModal(false);
  }, [setPreviewProduct, setIsOpenNoteModal]);

  const onSubmitAddingNote = useCallback(
    (note) => {
      setProductNote({ productId: previewProduct.id, note: note });
      onClickCloseNoteModal();
    },
    [previewProduct, setProductNote, onClickCloseNoteModal],
  );

  const onClickDeleteNote = useCallback(
    (product) => {
      setProductNote({ productId: product.id, note: '' });
    },
    [setProductNote],
  );

  const onClickOpenNoteModal = useCallback(
    (product) => {
      if (loggedIn) {
        setPreviewProduct(product);
        setIsOpenNoteModal(true);
      } else {
        gotoSignIn();
      }
    },
    [setIsOpenNoteModal, setPreviewProduct, loggedIn],
  );

  const sortOption = useMemo(() => {
    return [
      {
        label: translations(messages.bestSeller),
        value: 'BEST_SELLER',
        field: ProductSortField.RecentTotalSold,
        sortValue: SortDirection.Desc,
      },
      {
        label: translations(messages.newest),
        value: 'NEWEST',
        field: ProductSortField.CreatedAt,
        sortValue: SortDirection.Desc,
      },
      {
        label: translations(messages.ascPrice),
        value: `${ProductSortField.Price}_${SortDirection.Asc}`,
        field: ProductSortField.RegionPrice,
        sortValue: SortDirection.Asc,
      },
      {
        label: translations(messages.descPrice),
        value: `${ProductSortField.Price}_${SortDirection.Desc}`,
        field: ProductSortField.RegionPrice,
        sortValue: SortDirection.Desc,
      },
    ];
  }, [lang]);

  const handleAllChangeSearch = (values, pagination = {}) => {
    let sort;
    const subCategoryId = values?.subCategory?.map((category) => Number(category)) || undefined;
    const brandIds = values?.brands?.length ? values?.brands?.map((item) => Number(item)) : undefined;
    const origins = values?.origins?.length ? values?.origins?.map((item) => item) : undefined;
    if (values?.sort?.length) {
      const sortItem = sortOption.find((sort) => sort.value === values?.sort[0]);
      if (sortItem) {
        sort = [
          {
            field: sortItem.field,
            order: sortItem?.sortValue,
          },
        ];
      }
    }
    onSearch({
      search: {
        ...search,
        filter: {
          ...search.filter,
          subCategoryId: subCategoryId,
          brandIds: brandIds,
          origins: origins,
        },
        pagination: {
          page: 1,
          ...pagination,
        },
      },
      sort: sort,
      isFromCategory: false,
    });
    setPersistFilter(values);

    setTimeout(() => {
      const previousFilters = search.filter || {};
      const currentFilters = { ...search.filter, brandIds: brandIds, origins: origins };

      Object.keys(currentFilters).forEach((key) => {
        const previousValue = previousFilters[key];
        const currentValue = currentFilters[key];
        if (JSON.stringify(previousValue) !== JSON.stringify(currentValue)) {
          const action =
            !previousValue ||
            (Array.isArray(previousValue) &&
              Array.isArray(currentValue) &&
              previousValue?.length < currentValue?.length)
              ? 'add'
              : 'remove';
          sendGA4Event(EventName.SEARCH_PAGE_FILTER_APPLY, {
            filter_type: key,
            filter_value: currentValue,
            action: action,
          });
        }
      });

      if (sort && sort.length > 0 && JSON.stringify(search.sort) !== JSON.stringify(sort)) {
        sendGA4Event(EventName.SEARCH_PAGE_SORT_APPLY, {
          sort_option: `${sort[0].field}_${sort[0].order}`,
        });
      }
    }, 0);
  };

  const trackingFrom = useMemo<ITrackingFrom>(() => {
    switch (true) {
      case !!search.filter?.query: {
        return { productImpression: TrackingType.SearchResult, addToCart: TrackingAddToCartAddFromType.SearchResult };
      }
      case !!search.filter?.tags: {
        if (isRecommendationTag(search.filter?.tags)) {
          return {
            productImpression: TrackingType.CollectionRecommendation,
            addToCart: TrackingAddToCartAddFromType.CollectionRecommendation,
          };
        }
        return {
          productImpression: TrackingType.CollectionTag,
          addToCart: TrackingAddToCartAddFromType.CollectionTag,
        };
      }
      case !!search.filter?.categoryIds && location.pathname.slice(1).includes('/'): {
        return {
          productImpression: TrackingType.CollectionSubCategory,
          addToCart: TrackingAddToCartAddFromType.CollectionSubCategory,
        };
      }
      case !!category?.name: {
        return {
          productImpression: TrackingType.CollectionCategory,
          addToCart: TrackingAddToCartAddFromType.CollectionCategory,
        };
      }
      default: {
        return { productImpression: TrackingType.AllProduct, addToCart: TrackingAddToCartAddFromType.AllProduct };
      }
    }
  }, [search.filter, location.pathname]);

  const businessCategory = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('businessCategory');
  }, [location.search]);

  const meatadataKey = useMemo<IMetadataKeyData>(() => {
    if (search?.filter?.tags) {
      if (search.filter.tags[0] === recommendedForYouKey) {
        return {
          data: recommendedForYouKey,
          type: 'recommended',
          businessCategory: businessCategory || undefined,
        };
      }
      return {
        data: search?.filter?.tags?.[0],
        type: 'tags',
      };
    }
    if (search?.filter?.categoryIds) {
      return {
        data: search?.filter?.categoryIds?.[0],
        type: 'category',
      };
    }
    if (search?.filter?.query) {
      return {
        data: 'search',
        type: 'search',
        querySearch: search?.filter?.query,
      };
    }
    if (isEmpty(search?.filter) || isEmpty(search?.sort)) {
      return {
        data: 'market',
        type: 'page',
      };
    }
    return { type: 'empty', data: '' };
  }, [search, businessCategory]);

  const isProductSearch =
    meatadataKey.data === 'search' && !keywordData.keyword.some((key) => key === meatadataKey.querySearch);

  useEffect(() => {
    setCanClearFilter?.(false);
    if (location.pathname === '/market' && !isInitPage && !aggregationData?.loading) {
      const segment = getPathSegmentFromSlug(location);
      const searchQuery = createSearchQueryfromSlug(location, segment);
      const searchValue: QueryProductSearchArgs | undefined =
        searchQuery.length > 1 ? qs.parse(searchQuery) : undefined;
      const queryParsed = handleParseQueryParams(searchValue);
      const sortValue: any = [];
      if (queryParsed?.sort) {
        queryParsed?.sort?.forEach((sortItem) => {
          const currentValue = sortOption.find(
            (item) => item.field === sortItem.field && item.sortValue === sortItem.order,
          );
          sortValue.push(currentValue);
        });
      }
      form.setFieldsValue({
        brands: queryParsed?.filter?.brandIds || [],
        origins: queryParsed?.filter?.origins || [],
        subCategory:
          aggregationData?.subCategories?.length > 1
            ? queryParsed?.filter?.categoryIds || [0]
            : [aggregationData?.subCategories?.[0]?.value || 0],
        sort: sortValue?.[0]?.value ? [sortValue?.[0]?.value] : [],
      });
      const { categoryIds, query, ...rest } = queryParsed?.filter;
      setPersistFilter(form.getFieldsValue());
      if (
        Object.values(rest).some((item: any[]) => !!item?.length) ||
        sortValue?.length ||
        (categoryIds &&
          categoryIds?.length !== aggregationData?.subCategories?.filter((item) => item.value !== 0)?.length &&
          categoryIds?.[0] !== 0)
      ) {
        setCanClearFilter?.(true);
      }
      setIsInitPage(true);
    }
  }, [aggregationData?.loading, location]);

  return (
    <Viewport.Consumer>
      {({ width }) => {
        const isDesktop = width > 1024;
        return (
          <>
            {!!kamereoProducts?.length && <KamereoPromotionProducts products={kamereoProducts} />}
            {location.pathname === '/market' && (
              <>
                {isDesktop && (
                  <SearchFilter
                    onSearch={handleAllChangeSearch}
                    form={form}
                    aggregationData={aggregationData}
                    sortOption={sortOption}
                    canClearFilter={!!canClearFilter}
                    setCanClearFilter={setCanClearFilter}
                    searchData={result.data}
                  />
                )}
                {!isDesktop && (
                  <SearchFilterModal
                    onSearch={handleAllChangeSearch}
                    form={form}
                    aggregationData={aggregationData}
                    sortOption={sortOption}
                    openModal={openSearchFilter}
                    onCloseModal={() => setOpenSearchFilter?.(false)}
                    canClearFilter={!!canClearFilter}
                    setCanClearFilter={setCanClearFilter}
                    persistFilter={persistFilter}
                    searchData={result.data}
                  />
                )}
              </>
            )}
            <ProductListContainer ref={contentRef}>
              <RenderMetadataMarket
                noIndex={isProductSearch || !result?.data?.length}
                metadataKey={{ ...meatadataKey, metaFromDescription: refinedDescription?.meta }}
                page={search?.pagination?.page}
                breadCrumbData={breadCrumbDataMarkup}
                localLang={localLang ?? lang}
                alternateLang={alternateLang}
                category={category || ({} as Category)}
                productTags={productTags}
              />
              <Skeleton active paragraph={{ rows: 0 }} loading={description.loading} style={{ marginBottom: '12px' }}>
                <PageHeader asComponent="h1">
                  {refinedDescription.title || renderCategoryName(category, search, '', productTags)}
                </PageHeader>
              </Skeleton>
              <SubCategoryNavBarDesktopComponent />
              <Row
                gutter={[
                  { xs: 8, sm: 8, md: 16, lg: 24 },
                  {
                    xs: 8,
                    sm: 8,
                    md: 16,
                    lg: 24,
                  },
                ]}
              >
                {result?.data?.map((product, index) => (
                  <Col xs={12} sm={8} md={6} key={product?.id} style={{ display: 'flex' }}>
                    <NewMarketItem
                      vertialOnly
                      listName="Market Page Product List"
                      loggedIn={loggedIn}
                      gotoSignIn={gotoSignIn}
                      product={product}
                      loading={product?.loading}
                      favorite={product?.isFavorite}
                      note={product?.note}
                      maxQuantity={100}
                      added={addedProducts[product.id]}
                      adding={addingProducts[product.id]}
                      position={index}
                      permissions={permissions}
                      onToggleFavorite={() => onClickTogglingFavorite(product)}
                      onOpenNote={() => onClickOpenNoteModal(product)}
                      onDeleteNote={() => onClickDeleteNote(product)}
                      impressionProperties={{
                        type: trackingFrom.productImpression,
                        tag:
                          trackingFrom.productImpression === TrackingType.CollectionTag
                            ? search.filter?.tags?.[0]
                            : undefined,
                        keyword:
                          trackingFrom.productImpression === TrackingType.SearchResult
                            ? search.filter?.query
                            : undefined,
                      }}
                      addToCartProperties={{
                        addFrom: trackingFrom.addToCart,
                        tag:
                          trackingFrom.addToCart === TrackingAddToCartAddFromType.CollectionTag
                            ? search.filter?.tags?.[0]
                            : undefined,
                      }}
                      ga4Callback={(product: Product) => {
                        sendGA4Event(EventName.PRODUCT_CLICK_FROM_SEARCH, {
                          keyword: search?.filter?.query || '',
                          product_id: product.id,
                          product_name: product.name,
                          position: index + 1,
                          page_number: (search?.pagination?.page || 0) + 1,
                          price: product.price || 0,
                          supplier_id: KAMEREO_SUPPLIER_ID,
                        });
                      }}
                    />
                  </Col>
                ))}
              </Row>
              {result.data.length ? (
                <PaginationWrapper>
                  <Col span={24}>
                    <Pagination
                      itemRender={(page, type, origin) => {
                        if (type === 'next' || type === 'prev' || type === 'jump-prev' || type === 'jump-next') {
                          return origin;
                        }
                        const tags = search?.filter?.tags?.toString();

                        /* Create the category segment's name with hyphen instead of encode URL */
                        let parentCategorySlug = '';
                        if (category?.parent && category?.parent?.nameLocal) {
                          parentCategorySlug = convertStringToSlug(category?.parent.nameLocal);
                        }
                        const categorySlug = `${
                          parentCategorySlug ? `${parentCategorySlug}/` : ''
                        }${generateProductSlug(category?.nameLocal, `c${category?.id || 0}`, localLang)}`;
                        const query = search?.filter?.query?.toString();

                        /* Check if current pagination URL is come from category or market search */
                        const isFromCategory = !location.pathname.startsWith('/market');
                        if (!pathnameChecker?.isCategoryPage(location.pathname)) return origin;
                        return (
                          <PaginationLink
                            page={page}
                            isFromCategory={isFromCategory}
                            category={categorySlug}
                            tags={tags}
                            query={query}
                            lang={localLang}
                          />
                        );
                      }}
                      onChange={onChangePage}
                      total={result.totalResults}
                      pageSize={search.pagination.size || 40}
                      current={search.pagination.page || 1}
                      defaultCurrent={search.pagination.page || 1}
                      showSizeChanger={false}
                    />
                  </Col>
                </PaginationWrapper>
              ) : (
                <>
                  <FlexContainer>
                    <NotFoundImage src={NotFoundProductImage} />
                    <Title>
                      <FormattedMessage {...messages.notFound} />
                    </Title>
                    <ButtonWrapper>
                      <Button style={{ height: '40px' }} onClick={() => history.push('/')}>
                        <FormattedMessage {...messages.backToHome} />
                      </Button>
                      <Button
                        style={{ height: '40px' }}
                        type="primary"
                        onClick={() => setIsOpenRequestProductModal(true)}
                      >
                        <FormattedMessage {...messages.requestSellProduct} />
                      </Button>
                    </ButtonWrapper>
                  </FlexContainer>
                  {!!suggestedProducts.length && (
                    <>
                      <SuggestedListTitle>
                        <FormattedMessage {...messages.youMayAlsoLike} />
                      </SuggestedListTitle>
                      <ProductListRow
                        gutter={[
                          { xs: 8, sm: 8, md: 16, lg: 24 },
                          {
                            xs: 8,
                            sm: 8,
                            md: 16,
                            lg: 24,
                          },
                        ]}
                      >
                        {suggestedProducts.map((product, index) => (
                          <Col xs={12} md={8} xl={6} key={product?.id}>
                            <NewMarketItem
                              listName="Market Page Suggested Product List"
                              loggedIn={loggedIn}
                              gotoSignIn={gotoSignIn}
                              product={product}
                              loading={false}
                              favorite={product?.isFavorite}
                              note={product?.note}
                              maxQuantity={100}
                              added={addedProducts[product.id]}
                              adding={addingProducts[product.id]}
                              position={index}
                              permissions={permissions}
                              onToggleFavorite={() => onClickTogglingFavorite(product)}
                              onOpenNote={() => onClickOpenNoteModal(product)}
                              onDeleteNote={() => onClickDeleteNote(product)}
                              impressionProperties={{
                                type: trackingFrom.productImpression,
                                tag:
                                  trackingFrom.productImpression === TrackingType.CollectionTag
                                    ? search.filter?.tags?.[0]
                                    : undefined,
                                keyword:
                                  trackingFrom.productImpression === TrackingType.SearchResult
                                    ? search.filter?.query
                                    : undefined,
                              }}
                              addToCartProperties={{
                                addFrom: trackingFrom.addToCart,
                                tag:
                                  trackingFrom.addToCart === TrackingAddToCartAddFromType.CollectionTag
                                    ? search.filter?.tags?.[0]
                                    : undefined,
                              }}
                              vertialOnly
                            />
                          </Col>
                        ))}
                      </ProductListRow>
                    </>
                  )}
                </>
              )}
              <ModalNote
                headerLabel={previewProduct && previewProduct.name}
                isOpen={isOpenNoteModal}
                confirmLoading={noteItemLoading}
                onSubmit={onSubmitAddingNote}
                onClose={onClickCloseNoteModal}
                note={previewProduct ? previewProduct.note : ''}
              />
            </ProductListContainer>
            <ButtonRequestNewProduct type="default" onClick={() => setIsOpenRequestProductModal(true)}>
              <FormattedMessage {...messages.requestSellProduct} />
            </ButtonRequestNewProduct>
            {description.loading ? (
              <SkeletonWrapper>
                <Skeleton loading={true} paragraph={{ rows: 8, width: '100%' }} active />
              </SkeletonWrapper>
            ) : (
              refinedDescription.description && (
                <ProductDescriptionWrapper>
                  <PageHeader semibold asComponent="h2" style={{ margin: 0 }}>
                    {renderCategoryName(category, search, '', productTags)}
                  </PageHeader>
                  <PageDescription
                    isShowFullDescription={isShowFullDescription}
                    dangerouslySetInnerHTML={{
                      __html: refinedDescription.description,
                    }}
                  />
                  <ReadMoreButton
                    isShowFullDescription={isShowFullDescription}
                    handleReadMore={handleReadMore}
                    Icon={DownArrow}
                    messages={messages}
                  />
                </ProductDescriptionWrapper>
              )
            )}
            <RequestNewProductPopup
              open={isOpenRequestProductModal}
              setModalStatus={setIsOpenRequestProductModal}
              sendRequestNewProduct={sendRequestNewProduct}
              storeId={store?.id || ''}
              userEmail={user?.email || ''}
            />
          </>
        );
      }}
    </Viewport.Consumer>
  );
};

const mapStateToProps = createStructuredSelector<RootState, IStateProps>({
  marketpage: selectMarketPage(),
  category: selectSelectedCategory(),
  addedProducts: selectAddedProducts(),
  addingProducts: selectAddingProducts(),
  permissions: selectUserPermissions(),
  breadCrumbDataMarkup: selectBreadCrumbDataMarkup(),
  store: selectStore(),
  user: selectUser(),
  productTags: selectProductTags(),
  aggregationData: selectAggregationData(),
});

function mapDispatchToProps(dispatch: Dispatch): IDispatchProps {
  return {
    onSearch: (data) => dispatch(applySearch(data)),
    onToggleFavorite: (data) => dispatch(setFavorite(data)),
    setProductNote: (data: any) => dispatch(setProductNote(data)),
    gotoSignIn: () => dispatch(setOpenSigninPopup(true)),
    sendRequestNewProduct: (data) => dispatch(sendRequestNewProductAction(data)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

// <OwnProps> restricts access to the HOC's other props. This component must not do anything with reducer hoc
const withReducer = injectReducer<Props>({ key: 'marketPage', reducer: reducer });
// <OwnProps> restricts access to the HOC's other props. This component must not do anything with saga hoc
const withSaga = injectSaga<Props>({ key: 'marketPage', saga: saga });

export default compose(withReducer, withSaga, withConnect)(MarketPage);
